import HojaLifecycle from '../../assets/images/Lifecycle_Hoja_512x512.png';
import { FallingLeavesBackground } from './FallingLeavesBackground';
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  IconButton,
  InputAdornment,
  Container,
  Paper,
  Divider,
} from '@mui/material';
import { Visibility, VisibilityOff, Login as LoginIcon } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuth } from '../../context/AuthContext';
import { LoginRequest, GoogleCountrySelectionOption } from '../../types';
import { GoogleSignInButton } from './GoogleSignInButton';
import { CompanySelector } from './CompanySelector';

// Esquema de validación
const loginSchema = yup.object({
  email: yup.string().email('Debe ser un email válido').required('El email es requerido'),
  password: yup.string().required('La contraseña es requerida'),
});

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, loginWithGoogle, selectCountryAfterGoogleLogin, isLoading, error } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  // Phase 1B (CLAUDE.md §1/§2): set only for the rare dual-account case,
  // where /auth/google returned options instead of a token. Held in local
  // component state (not AuthContext) - no session exists yet at this point,
  // this is a pre-auth, one-shot step, not persisted app state.
  const [pendingGoogleIdToken, setPendingGoogleIdToken] = useState<string | null>(null);
  const [countryOptions, setCountryOptions] = useState<GoogleCountrySelectionOption[] | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginRequest>({
    resolver: yupResolver(loginSchema),
  });

  const onSubmit = async (data: LoginRequest) => {
    try {
      await login(data);
      navigate('/dashboard');
    } catch (error) {
      // Error ya manejado por el contexto
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleRegisterClick = () => {
    navigate('/register');
  };

  const handleGoogleCredential = async (idToken: string) => {
    try {
      const options = await loginWithGoogle(idToken);
      if (options && options.length > 0) {
        // Dual-account case (CLAUDE.md §1/§2, ~1-2 people): show the
        // company picker instead of navigating through.
        setPendingGoogleIdToken(idToken);
        setCountryOptions(options);
        return;
      }
      navigate('/dashboard');
    } catch (error) {
      // Error ya manejado por el contexto
    }
  };

  const handleCompanySelect = async (country: string) => {
    if (!pendingGoogleIdToken) return;
    try {
      await selectCountryAfterGoogleLogin(pendingGoogleIdToken, country);
      navigate('/dashboard');
    } catch (error) {
      // Error ya manejado por el contexto
    } finally {
      setPendingGoogleIdToken(null);
      setCountryOptions(null);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      width: '100vw',
      position: 'fixed',
      top: 0,
      left: 0,
      zIndex: 0,
      overflow: 'auto',
      background: 'rgba(66,154,45,0.07)', // verde muy transparente
    }}>
      <FallingLeavesBackground />
      {/* Contenido principal login */}
      <Container component="main" maxWidth="xs" sx={{ position: 'relative', zIndex: 2 }}>
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper elevation={3} sx={{ padding: 4, width: '100%', background: 'rgba(255,255,255,0.95)', position: 'relative' }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <img src={HojaLifecycle} alt="Hoja LifeCycle" style={{ width: 120, marginBottom: 12 }} />
              <Typography component="h1" variant="h4" gutterBottom align="center">
                Presupuestos LifeCycle
              </Typography>
              <Typography component="h2" variant="h5" gutterBottom>
                Iniciar Sesión
              </Typography>

              {error && (
                <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                  {error}
                </Alert>
              )}

              {countryOptions ? (
                <CompanySelector
                  options={countryOptions}
                  onSelect={handleCompanySelect}
                  disabled={isLoading}
                />
              ) : (
              <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 1, width: '100%' }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="email"
                  label="Email"
                  type="email"
                  autoComplete="email"
                  autoFocus
                  {...register('email')}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="Contraseña"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  {...register('password')}
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="mostrar contraseña"
                          onClick={handleTogglePasswordVisibility}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={isLoading}
                  startIcon={isLoading ? undefined : <LoginIcon />}
                >
                  {isLoading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
                </Button>
                <Box textAlign="center">
                  <Typography variant="body2">
                    ¿No tienes cuenta?{' '}
                    <Button
                      variant="text"
                      size="small"
                      onClick={handleRegisterClick}
                      sx={{ textTransform: 'none' }}
                    >
                      Regístrate aquí
                    </Button>
                  </Typography>
                </Box>
              </Box>
              )}

              {!countryOptions && (
                <>
                  <Divider sx={{ width: '100%', my: 2 }}>o</Divider>
                  <GoogleSignInButton onCredential={handleGoogleCredential} disabled={isLoading} />
                </>
              )}
            </Box>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};
